import { motion } from 'framer-motion';
import { ArrowRight, Check, Star, X } from 'lucide-react';
import { useState } from 'react';
import SEOHead from '../components/SEOHead';

const Pricing = () => {
  const [isAnnual, setIsAnnual] = useState(true);

  const plans = [
    {
      name: 'Builder',
      price: { monthly: 0, annual: 0 },
      description: 'Sandbox keys for prototyping and testing',
      features: [
        'OID4VCI issuer (sandbox)',
        'OID4VP verifier (sandbox)',
        'Wallet SDK (dev mode)',
        'Community support',
        'Rate limits apply',
      ],
      notIncluded: ['Prod SLA', 'Advanced policies', 'Dedicated support'],
      popular: false,
    },
    {
      name: 'Growth',
      price: 'Contact Sales',
      description: 'Production-ready with higher limits and SLAs',
      features: [
        'Managed issuers & verifiers',
        'Regional data residency',
        'Key management (KMS/HSM)',
        'Policy engine & webhooks',
        'Priority support',
        'Audit logs & observability',
      ],
      notIncluded: ['Dedicated account manager'],
      popular: true,
    },
    {
      name: 'Enterprise',
      price: 'Contact Sales',
      description: 'Custom controls, isolation and support',
      features: [
        'Private cloud or on‑prem options',
        'Custom DID methods & registries',
        'SAML/SCIM SSO & RBAC',
        'Dedicated account manager',
        'SLA guarantees',
        'Security review & pen‑tests',
      ],
      notIncluded: [],
      popular: false,
    },
  ];

  const features = [
    'Document Management',
    'Workflow Automation',
    'Team Collaboration',
    'Security & Compliance',
    'API Access',
    'Custom Integrations',
    'Advanced Analytics',
    'Priority Support',
    'Dedicated Account Manager',
    'SLA Guarantees',
  ];

  const pricingStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: 'Documint Platform',
    description:
      'Self-Sovereign Identity platform with flexible pricing plans from free sandbox to enterprise solutions.',
    url: 'https://documint.id/pricing',
    brand: {
      '@type': 'Brand',
      name: 'Documint',
    },
    offers: plans.map(plan => ({
      '@type': 'Offer',
      name: plan.name,
      description: plan.description,
      price: typeof plan.price === 'object' ? plan.price.annual : plan.price,
      priceCurrency: 'USD',
      availability: 'https://schema.org/InStock',
    })),
  };

  return (
    <div className="min-h-screen pt-20">
      <SEOHead
        title="Pricing Plans - Self-Sovereign Identity Platform"
        description="Choose the perfect SSI plan for your needs. Free sandbox for developers, growth plans for production, enterprise solutions for large organizations. Start building today."
        keywords="SSI pricing, verifiable credentials pricing, digital identity cost, DID platform pricing, identity platform plans, free SSI sandbox, enterprise identity solutions"
        url="https://documint.id/pricing"
        structuredData={pricingStructuredData}
      />
      {/* Hero Section */}
      <section className="bg-surface-100 py-20">
        <div className="container-custom text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-4xl lg:text-5xl font-bold text-white mb-6">
              Simple, Transparent Pricing
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-8">
              Start free in the sandbox. Scale with production SLAs when
              you&apos;re ready.
            </p>

            {/* Billing Toggle */}
            <div className="flex items-center justify-center space-x-4 mb-8">
              <span
                className={`text-sm font-medium ${
                  !isAnnual ? 'text-white' : 'text-gray-400'
                }`}
              >
                Monthly
              </span>
              <button
                onClick={() => setIsAnnual(!isAnnual)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  isAnnual ? 'bg-primary-600' : 'bg-white/20'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    isAnnual ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
              <span
                className={`text-sm font-medium ${
                  isAnnual ? 'text-white' : 'text-gray-400'
                }`}
              >
                Annual
                <span className="ml-1 text-primary-600 font-bold">
                  Save 20%
                </span>
              </span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="py-20 bg-white">
        <div className="container-custom">
          <div className="grid lg:grid-cols-3 gap-8">
            {plans.map((plan, index) => (
              <motion.div
                key={plan.name}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className={`relative rounded-2xl p-8 ${
                  plan.popular
                    ? 'bg-primary-600 text-white shadow-2xl scale-105'
                    : 'bg-gray-50 border-2 border-gray-200'
                }`}
              >
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <span className="bg-yellow-400 text-gray-900 px-4 py-2 rounded-full text-sm font-semibold flex items-center">
                      <Star className="w-4 h-4 mr-1" />
                      Most Popular
                    </span>
                  </div>
                )}

                <div className="text-center mb-8">
                  <h3
                    className={`text-2xl font-bold mb-2 ${
                      plan.popular ? 'text-white' : 'text-gray-900'
                    }`}
                  >
                    {plan.name}
                  </h3>
                  <p
                    className={`text-sm mb-6 ${
                      plan.popular ? 'text-primary-100' : 'text-gray-600'
                    }`}
                  >
                    {plan.description}
                  </p>

                  <div className="mb-6">
                    <span
                      className={`text-4xl font-bold ${
                        plan.popular ? 'text-white' : 'text-gray-900'
                      }`}
                    >
                      {typeof plan.price === 'string'
                        ? plan.price
                        : `$${
                            isAnnual ? plan.price.annual : plan.price.monthly
                          }`}
                    </span>
                    {typeof plan.price === 'object' && (
                      <span
                        className={`text-lg ${
                          plan.popular ? 'text-primary-100' : 'text-gray-600'
                        }`}
                      >
                        /month
                      </span>
                    )}
                  </div>

                  <a
                    href="https://app.passhub.documint.id/signup"
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`w-full py-3 px-6 rounded-lg font-semibold transition-colors duration-200 inline-block text-center ${
                      plan.popular
                        ? 'bg-white text-primary-600 hover:bg-gray-100'
                        : 'bg-primary-600 text-white hover:bg-primary-700'
                    }`}
                  >
                    Start Free Trial
                  </a>
                </div>

                <div className="space-y-4">
                  <h4
                    className={`font-semibold mb-4 ${
                      plan.popular ? 'text-white' : 'text-gray-900'
                    }`}
                  >
                    What&apos;s included:
                  </h4>
                  {plan.features.map((feature, featureIndex) => (
                    <div
                      key={featureIndex}
                      className="flex items-center space-x-3"
                    >
                      <Check
                        className={`w-5 h-5 flex-shrink-0 ${
                          plan.popular ? 'text-green-400' : 'text-green-500'
                        }`}
                      />
                      <span
                        className={`text-sm ${
                          plan.popular ? 'text-primary-100' : 'text-gray-700'
                        }`}
                      >
                        {feature}
                      </span>
                    </div>
                  ))}

                  {plan.notIncluded.length > 0 && (
                    <>
                      <h4
                        className={`font-semibold mb-4 mt-6 ${
                          plan.popular ? 'text-white' : 'text-gray-900'
                        }`}
                      >
                        Not included:
                      </h4>
                      {plan.notIncluded.map((feature, featureIndex) => (
                        <div
                          key={featureIndex}
                          className="flex items-center space-x-3"
                        >
                          <X
                            className={`w-5 h-5 flex-shrink-0 ${
                              plan.popular ? 'text-red-400' : 'text-red-500'
                            }`}
                          />
                          <span
                            className={`text-sm ${
                              plan.popular
                                ? 'text-primary-100'
                                : 'text-gray-500'
                            }`}
                          >
                            {feature}
                          </span>
                        </div>
                      ))}
                    </>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-white">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Get answers to common questions about our pricing and plans.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Can I change my plan at any time?
                </h3>
                <p className="text-gray-600">
                  Yes, you can upgrade or downgrade your plan at any time.
                  Changes will be prorated and reflected in your next billing
                  cycle.
                </p>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Is there a free trial?
                </h3>
                <p className="text-gray-600">
                  Yes, all plans include a 14-day free trial with full access to
                  all features. No credit card required to start.
                </p>
              </div>
            </div>
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  What payment methods do you accept?
                </h3>
                <p className="text-gray-600">
                  We accept all major credit cards, PayPal, and bank transfers
                  for annual plans. Enterprise customers can also pay by
                  invoice.
                </p>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Do you offer custom pricing?
                </h3>
                <p className="text-gray-600">
                  Yes, for Enterprise customers we offer custom pricing based on
                  your specific needs and usage requirements.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-primary-600">
        <div className="container-custom text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">
              Ready to Get Started?
            </h2>
            <p className="text-xl text-primary-100 mb-8 max-w-2xl mx-auto">
              Join thousands of businesses that trust Documint for their
              document management needs. Start your free trial today.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="https://app.passhub.documint.id/signup"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-white text-primary-600 font-semibold py-3 px-8 rounded-lg hover:bg-gray-100 transition-colors duration-200 inline-flex items-center"
              >
                Start Free Trial
                <ArrowRight size={20} className="ml-2" />
              </a>
              <a
                href="/contact"
                className="border-2 border-white text-white font-semibold py-3 px-8 rounded-lg hover:bg-white hover:text-primary-600 transition-colors duration-200"
              >
                Contact Sales
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Pricing;
